import React, { useMemo, useState } from 'react';

import { Box, Card, CardActionArea, Chip, Grid, InputAdornment, OutlinedInput, Typography, useTheme } from '@mui/material';

import {
    IconSearch,
    IconUsers,
    IconBell,
    IconPalette,
    IconChevronRight,
    IconShield,
    IconBuilding,
    IconLayoutGrid,
    IconForms
} from '@tabler/icons';

import { motion } from 'framer-motion';

import { useHistory } from 'react-router-dom';

const MotionCard = motion(Card);

const ADMIN_ITEMS = [
    {
        title: 'Minha Empresa',
        description: 'Empresas e filiais do seu grupo (matriz/filiais internas).',
        icon: IconBuilding,
        color: '#16a34a',
        route: '/dashboard/config/companies/view',
        badge: 'Estrutura',
        beta: false
    },
    {
        title: 'Cadastros Auxiliares',
        description: 'Unidades, moedas, geografia, bancos, formas de pagamento e mais.',
        icon: IconLayoutGrid,
        color: '#0891b2',
        route: '/dashboard/config/references',
        badge: 'Estrutura',
        beta: false
    },
    {
        title: 'Usuários do Sistema',
        description: 'Gerencie permissões, acessos e contas administrativas.',
        icon: IconUsers,
        color: '#2563eb',
        route: '/dashboard/admin/users',
        badge: 'Equipe',
        beta: false
    },
    {
        title: 'Campos Customizados',
        description: 'Defina campos extras pros cadastros de Empresas e Clientes.',
        icon: IconForms,
        color: '#7c3aed',
        route: '/dashboard/config/custom-fields',
        badge: 'Cadastros',
        beta: false
    },
    {
        title: 'Notificações',
        description: 'Configure os alertas de notificações.',
        icon: IconBell,
        color: '#C2C142',
        route: '/dashboard/admin/notifications',
        badge: 'Notificações',
        beta: false
    },
    {
        title: 'Customização',
        description: 'Ajuste temas, aparência e branding da plataforma.',
        icon: IconPalette,
        color: '#db2777',
        route: '/dashboard/config/custom',
        badge: 'Tema',
        beta: false
    }
];

const AdministrationCenter = () => {
    const theme = useTheme();

    const history = useHistory();

    const [search, setSearch] = useState('');

    const filteredItems = useMemo(() => {
        if (!search.trim()) {
            return ADMIN_ITEMS;
        }

        const term = search.toLowerCase();

        return ADMIN_ITEMS.filter(
            (item) =>
                item.title.toLowerCase().includes(term) ||
                item.description.toLowerCase().includes(term) ||
                item.badge.toLowerCase().includes(term)
        );
    }, [search]);

    return (
        <Box>
            {/* HERO HEADER */}

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '28px',
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #111827 0%, #1e293b 100%)'
                            : 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.08)'
                }}
            >
                {/* BG EFFECT */}

                <Box
                    sx={{
                        position: 'absolute',
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(168,85,247,0.12))',
                        top: -120,
                        right: -80,
                        filter: 'blur(30px)'
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Chip
                        icon={<IconShield size={14} />}
                        label="Central Administrativa"
                        sx={{
                            mb: 2,
                            fontWeight: 700,
                            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff',
                            backdropFilter: 'blur(10px)'
                        }}
                    />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: {
                                xs: '2rem',
                                md: '3.2rem'
                            },
                            lineHeight: 1.1,
                            mb: 1.5,
                            letterSpacing: '-2px',
                            background: 'linear-gradient(90deg, #2563eb, #9333ea)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Administração do Sistema
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            maxWidth: 720,
                            fontSize: '1rem',
                            lineHeight: 1.8,
                            color: 'text.secondary',
                            mb: 4
                        }}
                    >
                        Gerencie usuários do sistema, notificações e a aparência da plataforma em um único lugar.
                    </Typography>

                    {/* SEARCH */}

                    <OutlinedInput
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar configuração..."
                        startAdornment={
                            <InputAdornment position="start">
                                <IconSearch size={20} />
                            </InputAdornment>
                        }
                        sx={{
                            maxWidth: 720,
                            borderRadius: '18px',
                            height: 60,
                            px: 1,
                            backdropFilter: 'blur(12px)',
                            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#ffffff',
                            boxShadow: theme.palette.mode === 'dark' ? '0 6px 25px rgba(0,0,0,0.3)' : '0 6px 25px rgba(15,23,42,0.08)',
                            '& input': {
                                fontSize: '1rem'
                            }
                        }}
                    />
                </Box>
            </Box>

            {/* GRID */}

            <Grid container spacing={3}>
                {filteredItems.map((item, index) => {
                    const Icon = item.icon;

                    return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={item.title}>
                            <MotionCard
                                initial={{
                                    opacity: 0,
                                    y: 20
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0
                                }}
                                transition={{
                                    delay: index * 0.03,
                                    duration: 0.3
                                }}
                                whileHover={{
                                    y: -6
                                }}
                                sx={{
                                    height: '100%',
                                    borderRadius: '24px',
                                    overflow: 'hidden',
                                    border:
                                        theme.palette.mode === 'dark'
                                            ? '1px solid rgba(255,255,255,0.06)'
                                            : '1px solid rgba(15,23,42,0.06)',
                                    background:
                                        theme.palette.mode === 'dark' ? 'linear-gradient(180deg,#111827 0%,#0f172a 100%)' : '#ffffff',
                                    boxShadow:
                                        theme.palette.mode === 'dark' ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(15,23,42,0.06)',
                                    transition: 'all 0.25s ease'
                                }}
                            >
                                <CardActionArea
                                    onClick={() => history.push(item.route)}
                                    sx={{
                                        height: '100%',
                                        p: 3
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 3
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 62,
                                                height: 62,
                                                borderRadius: '18px',
                                                background: `linear-gradient(135deg, ${item.color}22, ${item.color}10)`,
                                                border: `1px solid ${item.color}33`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: `0 8px 20px ${item.color}22`
                                            }}
                                        >
                                            <Icon size={30} color={item.color} />
                                        </Box>

                                        {item.beta && (
                                            <Chip
                                                label="Beta"
                                                size="small"
                                                color="warning"
                                                sx={{
                                                    fontWeight: 700
                                                }}
                                            />
                                        )}

                                        <Chip
                                            label={item.badge}
                                            size="small"
                                            sx={{
                                                fontWeight: 700,
                                                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f8fafc'
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 800,
                                            mb: 1.5,
                                            fontSize: '1.05rem'
                                        }}
                                    >
                                        {item.title}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                            lineHeight: 1.8,
                                            minHeight: 70
                                        }}
                                    >
                                        {item.description}
                                    </Typography>

                                    <Box
                                        sx={{
                                            mt: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: item.color,
                                            fontWeight: 700,
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        Abrir módulo
                                        <IconChevronRight size={16} />
                                    </Box>
                                </CardActionArea>
                            </MotionCard>
                        </Grid>
                    );
                })}
            </Grid>

            {/* EMPTY */}

            {filteredItems.length === 0 && (
                <Box
                    sx={{
                        mt: 8,
                        textAlign: 'center',
                        py: 10,
                        borderRadius: '28px',
                        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                        border: theme.palette.mode === 'dark' ? '1px dashed rgba(255,255,255,0.08)' : '1px dashed #cbd5e1'
                    }}
                >
                    <IconSearch
                        size={54}
                        color="#94a3b8"
                        style={{
                            marginBottom: 12
                        }}
                    />

                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            mb: 1
                        }}
                    >
                        Nenhuma configuração encontrada
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary'
                        }}
                    >
                        Tente buscar outro termo.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default AdministrationCenter;
